import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDocumentMetadata } from '../../fetch/fetchMetadata';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import { logDocumentOpen } from '../../fetch/insightsHelper';

import { calculateDocumentPaneSize, DocumentNotFound, DocumentLoading } from '../documentViewCore';
import { useSelector } from 'react-redux';
import HasCoverDocumentView from './HasCoverDocument/HasCoverDocumentView';
import NoCoverDocumentView from './NoCoverDocument/NoCoverDocumentView';
import { useCheckForPassedInFamilyFilter, useIsSinglePage, useWindowDimensions } from '../../hooks';
import { parameterHelper } from '../../helpers';
import DocumentScrollView from '../scrollView/DocumentScrollView';
import { useSwitchFromScrollToSideBySide } from '../scrollView/scrollHelpers';
import { Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';

const defaultParameters = { page: null };
export default function DocumentView() {
  const { page } = parameterHelper.getUrlParameters(defaultParameters);
  const { height: viewportHeight, width: viewportWidth } = useWindowDimensions();
  const userLanguage = useSelector((state) => state.permissions.language);
  const { partNumber } = useParams();
  const [executedQuery, setExecutedQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useCheckForPassedInFamilyFilter();

  const [documentDimensions, setDocumentDimensions] = useState({ documentHeight: viewportHeight, documentWidth: viewportHeight / 1.3 });

  const { metadata, isLoading: isMetadataLoading, error } = useDocumentMetadata(partNumber);
  const { isSinglePageMode } = useIsSinglePage();
  useSwitchFromScrollToSideBySide(!isSinglePageMode && !!metadata && !isMetadataLoading);
  const { hasCover, title, sections, pages } = metadata;
  let pageRatio = null;
  if (!!pages && pages.length > 0) {
    pageRatio = pages[(!hasCover ? 0 : 1)].pageRatio;
  }

  useEffect(() => {
    setDocumentDimensions(calculateDocumentPaneSize(
      false,
      false,
      false,
      viewportWidth,
      viewportHeight,
      1.3));
  }, [viewportHeight, viewportWidth]);

  const defaultPage = hasCover ? -1 : 1;
  const currentPage = !!page ? page : defaultPage;
  const currentSection = sections?.filter(it => it.start <= currentPage && it.end >= currentPage)[0];

  useMemo(() => {
    if (!!userLanguage && !error) {
      const { activationKey } = parameterHelper.getUrlParameters({ activationKey: null });
      logDocumentOpen(partNumber, userLanguage, currentSection?.fileName, !!activationKey);
    }
  }, [partNumber, userLanguage, currentSection?.fileName, error]);

  useEffect(() => {
    if (!!title) {
      document.title = title;
    } else {
      document.title = 'Mercury Literature';
    }
  }, [title]);

  if (!!error) {
    return (
      <DocumentNotFound error={error} />
    );
  }

  if (isSinglePageMode) {
    return (
      <DocumentSearchFloater
        show={!!executedQuery && !showSearch}
        executedQuery={executedQuery}
        onClear={() => setExecutedQuery('')}
        onOpen={() => setShowSearch(true)}
      >
        <DocumentScrollView
          executedQuery={executedQuery}
          setExecutedQuery={setExecutedQuery}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
        />
      </DocumentSearchFloater >
    );
  }

  if (isMetadataLoading || hasCover === undefined || !pageRatio) {
    return (
      <DocumentLoading documentHeight={documentDimensions.documentHeight} />
    );
  }

  if (hasCover) {
    return (
      <DocumentSearchFloater
        show={!!executedQuery && !showSearch}
        executedQuery={executedQuery}
        onClear={() => setExecutedQuery('')}
        onOpen={() => setShowSearch(true)}
      >
        <HasCoverDocumentView
          partNumber={partNumber}
          metadata={metadata}
          pageRatio={pageRatio}
          executedQuery={executedQuery}
          setExecutedQuery={setExecutedQuery}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
        />
      </DocumentSearchFloater>
    );
  } else {
    return (
      <DocumentSearchFloater
        show={!!executedQuery && !showSearch}
        executedQuery={executedQuery}
        onClear={() => setExecutedQuery('')}
        onOpen={() => setShowSearch(true)}
      >
        <NoCoverDocumentView
          partNumber={partNumber}
          metadata={metadata}
          pageRatio={pageRatio}
          executedQuery={executedQuery}
          setExecutedQuery={setExecutedQuery}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
        />
      </DocumentSearchFloater>
    );
  }
}

function DocumentSearchFloater({ show, onClear, onOpen, executedQuery, children }) {
  const theme = useTheme();

  return (
    <>
      {show ? (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 2000,
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.primary.main}`,
        }}>
          <Button onClick={onOpen}>Search: {executedQuery}</Button>
          <Button onClick={onClear} style={{minWidth: 'unset'}}>
            <CloseIcon />
          </Button>
        </div>
      ) : null}
      {children}
    </>
  );
}